import React, { useMemo } from 'react';
import { Building, TrendingUp, AlertTriangle, Activity, Trophy, ChevronRight } from 'lucide-react';

// Palette de scores alignée sur le PDF (rouge/orange/vert)
const scoreBand = (score) => {
  if (score == null) return { label: 'Non évalué', color: 'slate', dot: 'bg-slate-300', text: 'text-slate-500' };
  if (score >= 80) return { label: 'Bon état',    color: 'green',  dot: 'bg-emerald-500', text: 'text-emerald-700' };
  if (score >= 60) return { label: 'À surveiller', color: 'yellow', dot: 'bg-amber-500',   text: 'text-amber-700' };
  if (score >= 40) return { label: 'Dégradé',      color: 'orange', dot: 'bg-orange-500',  text: 'text-orange-700' };
  return              { label: 'Prioritaire',    color: 'red',    dot: 'bg-red-500',     text: 'text-red-700' };
};

export default function GlobalHealthDashboard({ sites, clientName, onSiteClick }) {
  const stats = useMemo(() => {
    const scored = sites.filter(s => s.score_global != null);
    const total = sites.length;
    const evaluated = scored.length;
    const avg = evaluated ? scored.reduce((sum, s) => sum + s.score_global, 0) / evaluated : null;

    const bands = { good: 0, watch: 0, degraded: 0, priority: 0, notEvaluated: total - evaluated };
    scored.forEach(s => {
      const sc = s.score_global;
      if (sc >= 80) bands.good++;
      else if (sc >= 60) bands.watch++;
      else if (sc >= 40) bands.degraded++;
      else bands.priority++;
    });

    // Ranking : top 3 meilleurs et top 3 pires
    const sorted = [...scored].sort((a, b) => b.score_global - a.score_global);
    const best = sorted.slice(0, 3);
    const worst = [...sorted].reverse().slice(0, 3);

    return { total, evaluated, avg, bands, best, worst };
  }, [sites]);

  const avgBand = scoreBand(stats.avg);

  // Ne pas afficher si moins de 2 bâtiments
  if (stats.total < 2) return null;

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      data-testid="global-health-dashboard"
    >
      {/* Bandeau navy */}
      <div className="bg-gradient-to-br from-[#1C49A6] via-[#163C89] to-[#004AAD] px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 font-semibold">Bilan de santé global</p>
            <h3 className="text-lg font-bold text-white">{clientName || 'Mairie'} — {stats.total} bâtiments suivis</h3>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-white/60 font-semibold">Score patrimoine</p>
          {stats.avg != null ? (
            <p className="text-3xl font-bold text-white tabular-nums">
              {Math.round(stats.avg)}<span className="text-sm text-white/60 font-normal"> / 100</span>
            </p>
          ) : (
            <p className="text-lg text-white/70">Non évalué</p>
          )}
        </div>
      </div>

      {/* Corps : distribution + classement */}
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        {/* Distribution des états */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-slate-500" />
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Distribution</p>
          </div>
          <BandRow label="Bon état"     count={stats.bands.good}     total={stats.total} color="bg-emerald-500" />
          <BandRow label="À surveiller" count={stats.bands.watch}    total={stats.total} color="bg-amber-500" />
          <BandRow label="Dégradé"      count={stats.bands.degraded} total={stats.total} color="bg-orange-500" />
          <BandRow label="Prioritaire"  count={stats.bands.priority} total={stats.total} color="bg-red-500" />
          {stats.bands.notEvaluated > 0 && (
            <BandRow label="Non évalué" count={stats.bands.notEvaluated} total={stats.total} color="bg-slate-300" />
          )}
        </div>

        {/* Classement */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-amber-500" />
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Classement</p>
          </div>
          {stats.evaluated === 0 ? (
            <p className="text-sm text-slate-400 italic">
              Aucune consultation enregistrée. Lancez une première consultation pour établir un score.
            </p>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-[11px] font-semibold text-emerald-700 mb-1.5">MEILLEURS SCORES</p>
                {stats.best.map(s => (
                  <RankRow key={s.site_id} site={s} onClick={onSiteClick} tone="good" />
                ))}
              </div>
              {(() => {
                const bestIds = new Set(stats.best.map(s => s.site_id));
                const worstFiltered = stats.worst.filter(s => s.score_global < 60 && !bestIds.has(s.site_id));
                if (worstFiltered.length === 0) return null;
                return (
                  <div>
                    <p className="text-[11px] font-semibold text-red-700 mb-1.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> À TRAITER EN PRIORITÉ
                    </p>
                    {worstFiltered.slice(0, 3).map(s => (
                      <RankRow key={s.site_id} site={s} onClick={onSiteClick} tone="bad" />
                    ))}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Barre statut inférieure */}
      <div className={`px-6 py-3 flex items-center gap-2 text-sm border-t border-slate-100 bg-slate-50`}>
        <span className={`w-2 h-2 rounded-full ${avgBand.dot}`} />
        <span className={`font-semibold ${avgBand.text}`}>{avgBand.label}</span>
        <span className="text-slate-500">— {stats.evaluated} bâtiment(s) évalué(s) sur {stats.total}</span>
      </div>
    </div>
  );
}

function BandRow({ label, count, total, color }) {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <div className="mb-2.5" data-testid={`band-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="text-slate-500 tabular-nums">{count} <span className="text-slate-400">/ {total}</span></span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function RankRow({ site, onClick, tone }) {
  const band = tone === 'good'
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return (
    <button
      onClick={() => onClick?.(site.site_id)}
      className="w-full flex items-center justify-between py-2 px-3 mb-1.5 rounded-lg hover:bg-slate-50 transition-colors group text-left"
      data-testid={`rank-row-${site.site_id}`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Building className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{site.nom}</p>
          {site.type_site && <p className="text-[11px] text-slate-500 truncate">{site.type_site}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs font-bold px-2 py-1 rounded-md border ${band}`}>
          {Math.round(site.score_global)}<span className="text-[10px] font-normal opacity-70">/100</span>
        </span>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
      </div>
    </button>
  );
}
